import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/lib/supabase/AuthContext';
import { vaultConfig } from '@/lib/supabase/client';
import { cn } from '@/lib/utils/cn';
import { spring } from '@/lib/motion/presets';

/**
 * VaultLayout — the protected shell shown after authentication.
 *
 * Phase 1 ships this as a clean, minimal placeholder. Sidebar + grid +
 * item detail pages arrive in Phase 2. Right now it proves the
 * auth flow works end-to-end and gives the user something to land on
 * after the welcome screen.
 */
export function VaultLayout() {
  const { session, signOut } = useAuth();
  const navigate = useNavigate();

  async function handleSignOut() {
    await signOut();
    navigate('/welcome', { replace: true });
  }

  return (
    <div className="min-h-screen bg-bg text-ink">
      {/* Top bar */}
      <header className="sticky top-0 z-20 border-b border-line bg-bg/85 backdrop-blur-xl">
        <div className="mx-auto flex h-14 max-w-[1400px] items-center justify-between px-6">
          <div className="flex items-center gap-3">
            <NavLink to="/vault" className="flex items-center gap-2.5">
              <VaultMark />
              <span className="text-sm font-medium tracking-tight">
                {vaultConfig.displayName}&rsquo;s vault
              </span>
            </NavLink>
          </div>

          <nav className="hidden items-center gap-1 sm:flex">
            <NavTab to="/vault">All</NavTab>
            <NavTab to="/vault/files">Files</NavTab>
            <NavTab to="/vault/photos">Photos</NavTab>
            <NavTab to="/vault/notes">Notes</NavTab>
            <NavTab to="/vault/passwords">Passwords</NavTab>
            <NavTab to="/vault/cards">Cards</NavTab>
            <NavTab to="/vault/ids">IDs</NavTab>
            <NavTab to="/vault/keys">Keys</NavTab>
          </nav>

          <div className="flex items-center gap-2">
            <span className="hidden text-2xs text-ink-faint sm:inline">
              {session?.user?.email}
            </span>
            <motion.button
              whileHover={{ y: -1 }}
              whileTap={{ scale: 0.97 }}
              transition={spring}
              onClick={handleSignOut}
              className={cn(
                'rounded-md border border-line bg-surface px-3 py-1.5 text-xs',
                'text-ink-muted hover:text-ink hover:border-line-strong',
                'transition-colors',
              )}
            >
              Lock
            </motion.button>
          </div>
        </div>
      </header>

      {/* Body — placeholder for Phase 2 */}
      <main className="mx-auto max-w-[1400px] px-6 py-10">
        <Outlet />
      </main>
    </div>
  );
}

function NavTab({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      end={to === '/vault'}
      className={({ isActive }) =>
        cn(
          'relative rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
          isActive
            ? 'text-ink'
            : 'text-ink-muted hover:text-ink',
        )
      }
    >
      {({ isActive }) => (
        <>
          {children}
          {isActive && (
            <motion.span
              layoutId="vault-tab-indicator"
              className="absolute inset-x-2 -bottom-[15px] h-[2px] rounded-full bg-accent"
              transition={spring}
            />
          )}
        </>
      )}
    </NavLink>
  );
}

function VaultMark() {
  return (
    <span className="relative inline-flex size-7 items-center justify-center">
      <svg viewBox="0 0 28 28" className="size-7">
        <defs>
          <linearGradient id="vm" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#FF8A64" />
            <stop offset="100%" stopColor="#FF6A3D" />
          </linearGradient>
        </defs>
        <circle cx="14" cy="14" r="13" fill="none" stroke="url(#vm)" strokeWidth="1.5" />
        <circle cx="14" cy="14" r="6" fill="none" stroke="url(#vm)" strokeWidth="1.5" />
        <line x1="14" y1="3" x2="14" y2="6" stroke="url(#vm)" strokeWidth="1.5" strokeLinecap="round" />
        <line x1="14" y1="22" x2="14" y2="25" stroke="url(#vm)" strokeWidth="1.5" strokeLinecap="round" />
        <line x1="3" y1="14" x2="6" y2="14" stroke="url(#vm)" strokeWidth="1.5" strokeLinecap="round" />
        <line x1="22" y1="14" x2="25" y2="14" stroke="url(#vm)" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    </span>
  );
}